import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Lock, ArrowLeft, CheckCircle } from "lucide-react";

export default function ForgotPassword({ userType, onBack }) {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("request"); // "request" or "reset"
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const resetToken = searchParams.get("reset_token");
    if (resetToken) {
      setMode("reset");
    }
  }, [searchParams]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/forgot-password`, { email, user_type: userType });
      setSent(true);
      toast.success("If an account exists, you'll receive a reset link.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const resetToken = searchParams.get("reset_token");
      await axios.post(`${API}/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success("Password reset! You can now log in.");
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (mode === "reset") {
    return (
      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold">Set New Password</h3>
          <p className="text-sm text-muted-foreground">Enter your new password below</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="new-password"
              type="password"
              placeholder="Min 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="pl-10"
              data-testid="new-password-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id="confirm-password"
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10"
              data-testid="confirm-password-input"
            />
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-btn">
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    );
  }

  if (sent) {
    return (
      <div className="text-center space-y-4 py-4">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
        <h3 className="text-lg font-semibold">Check Your Email</h3>
        <p className="text-sm text-muted-foreground">
          If an account exists with <strong>{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.
        </p>
        <Button variant="outline" onClick={onBack} className="mt-4" data-testid="back-to-login-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleRequestReset} className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">Forgot Password?</h3>
        <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="forgot-email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="forgot-email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            data-testid="forgot-email-input"
          />
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="send-reset-link-btn">
        {loading ? "Sending..." : "Send Reset Link"}
      </Button>
      <Button type="button" variant="ghost" onClick={onBack} className="w-full" data-testid="back-to-login-link">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Login
      </Button>
    </form>
  );
}
